/**
 * Plate-loading math for barbell exercises.
 *
 * Given a target total load, the bar weight, and the plate denominations
 * available, compute the plates to load on EACH side of the bar (the loader
 * is symmetric — the same plates go on both sides).
 *
 * Greedy from heaviest to lightest. If the remaining weight can't be matched
 * exactly with the available plates, returns the closest under-or-equal stack
 * plus a `remainder` so the caller can render "+1.25 lb under" or similar.
 *
 * @param {number} target — total prescribed weight in lbs
 * @param {number} barWeight — bar weight in lbs (e.g. 45 for a straight bar)
 * @param {number[]} plates — available plate denominations (one side's inventory)
 * @returns {{plates: Array<{plate:number, count:number}>, remainder:number,
 *           exact:boolean, perSide:number} | null}
 *          Returns null when target < barWeight (just the bar, no plates).
 */
export function platesPerSide(target, barWeight, plates) {
    if (target == null || barWeight == null) return null;
    const t = Number(target);
    const bar = Number(barWeight) || 0;
    if (!Number.isFinite(t) || t <= 0) return null;
    // If target ≤ bar, no plates needed
    if (t <= bar) {
        return { plates: [], remainder: 0, exact: t === bar, perSide: 0 };
    }
    const perSide = (t - bar) / 2;
    const available = (plates && plates.length ? plates : []).slice()
        .map(Number)
        .filter((p) => Number.isFinite(p) && p > 0)
        .sort((a, b) => b - a);
    let remaining = perSide;
    const used = [];
    for (const p of available) {
        if (remaining <= 0) break;
        const count = Math.floor(remaining / p);
        if (count > 0) {
            used.push({ plate: p, count });
            // Avoid FP drift on common .5 / .25 plates
            remaining = Math.round((remaining - count * p) * 1000) / 1000;
        }
    }
    return {
        plates: used,
        remainder: Math.max(0, remaining),
        exact: remaining < 0.01,
        perSide,
    };
}

/**
 * Render a plate breakdown as a compact human-readable string.
 * Example: [{plate:35,count:1},{plate:5,count:1},{plate:2.5,count:1}]
 *   → "35×1, 5×1, 2.5×1"
 * If remainder > 0, appends "+ {remainder} short".
 */
export function formatPlateBreakdown(result) {
    if (!result) return "—";
    if (result.perSide === 0) {
        return result.exact ? "Just the bar" : "—";
    }
    if (!result.plates.length) return "—";
    const parts = result.plates.map((p) => {
        const plateStr = p.plate % 1 === 0 ? String(p.plate) : p.plate.toFixed(2).replace(/\.?0+$/, "");
        return `${plateStr}×${p.count}`;
    });
    let out = parts.join(", ");
    if (!result.exact && result.remainder > 0) {
        out += ` (+${result.remainder.toFixed(2).replace(/\.?0+$/, "")} short)`;
    }
    return out;
}
